import { zodResolver } from '@hookform/resolvers/zod';
import {
  ApiError,
  type UserResource,
  UsersController_clearLockout_v1,
  UsersController_deactivate_v1,
  UsersController_reactivate_v1,
  UsersController_update_v1,
  type UsersController_update_v1Body,
} from '@repo/api-client';
import { Alert, AlertDescription } from '@repo/ui/components/alert';
import { Badge } from '@repo/ui/components/badge';
import { Button } from '@repo/ui/components/button';
import {
  Dialog,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@repo/ui/components/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@repo/ui/components/form';
import { Input } from '@repo/ui/components/input';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@repo/ui/components/select';
import { IconAlertTriangle } from '@tabler/icons-react';
import { useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link } from 'react-router';
import { z } from 'zod';

import { useApiClient } from '@/api/api-client';
import { messageForFailure } from '@/api/failure';
import { useSession } from '@/auth/session';
import { optionalPinSchema } from '@/lib/field-schemas';
import { IDEMPOTENCY_KEY_HEADER, newIdempotencyKey } from '@/lib/idempotency-key';
import { PasswordInput } from '@/routes/PasswordInput';

import { fieldLabel, fieldSurface, FormDialogContent } from '../auth/AuthFrame';
import { paths } from '../paths';
import {
  hintForRole,
  isDeactivated,
  isLockedOut,
  labelForRole,
  USER_ROLE_ITEMS,
  USER_ROLES,
  type UserRole,
} from './user-roles';

/**
 * One person, opened from the list: `PATCH /v1/users/{id}`, the two lifecycle
 * routes, and the owner's lockout override.
 *
 * **`staffCode` is not offered, and its absence is the rule.** `UpdateUserDto`
 * carries `displayName`, `role`, `password` and `pin` and nothing else, because
 * a staff code names who did something in records that outlive employment
 * (FR-101a) — editing one would silently re-point every such record at a code
 * its reader does not recognise. It is shown as what it is, the person's
 * identity, with one line saying why it holds still.
 *
 * **Two refusals, and each is rendered where it is about.**
 * `IDENTITY_ROLE_ESCALATION` and a demotion's `IDENTITY_LAST_OWNER` are both
 * statements about the role, so both are described by the role control rather
 * than by a banner over the whole form: the difference is between correcting one
 * control and re-reading five to work out which one the server meant. A
 * deactivation's `IDENTITY_LAST_OWNER` cannot be about a field — it is about the
 * question the confirmation just asked — so it replaces the question with the
 * explanation and withdraws the offer, because the answer cannot become yes
 * until somebody else holds the owner role.
 *
 * **Two of the API's rules are known in advance and are not offered.** Nobody
 * may change their own role, and nobody may set their own credential through
 * this route — the API refuses both whoever asks, so for the signed-in person
 * the role control is disabled with the reason beside it and the credential
 * fields are replaced by a pointer at `/change-credential`, which re-proves the
 * current password first. `docs/ui-conventions.md` asks for exactly this: hide
 * what cannot be used rather than showing it and letting the API refuse.
 *
 * Nothing is pre-empted about the *last owner*, and that is the other half of
 * the same judgement. Whether this person is the last one is a fact about rows
 * this dialog has not read, and a count carried down from a list loaded a minute
 * ago would be a guess wearing the clothes of a rule. The server holds
 * `FOR UPDATE` across that question; the screen explains its answer.
 *
 * **The copy is never written here.** `messageForFailure` turns a code into a
 * sentence and it is the only thing that does; the code is what is branched on,
 * never the message and never the status. A 409 from a proxy is not this
 * refusal.
 *
 * The confirmation is a stage of this dialog rather than a second one on top of
 * it, for the reason `EditOutletDialog` gives: two stacked dialogs put a focus
 * trap inside a focus trap for a question about the thing already open.
 */

const NAME_REQUIRED = 'Enter the name to show beside their actions.';

/**
 * What this dialog will send.
 *
 * No `staffCode`, because `UpdateUserDto` has none — see the note above. The PIN
 * rule is imported rather than restated, as it is on the create form; the two
 * disagreeing was the failure `lib/field-schemas.ts` exists to prevent, and it
 * would have surfaced as somebody unable to change a PIN they were issued.
 */
const editUserSchema = z.object({
  displayName: z.string().trim().min(1, NAME_REQUIRED),
  role: z.custom<UserRole>(),
  password: z.string(),
  pin: optionalPinSchema,
});

type EditUserValues = z.infer<typeof editUserSchema>;

/** Which question the dialog is currently asking. One value, so two cannot overlap. */
type Stage = 'editing' | 'confirming-deactivate' | 'refused-last-owner';

export function EditUserDialog({
  user,
  onOpenChange,
  onUpdated,
  onDeactivated,
  onReactivated,
  onLockoutCleared,
}: {
  user: UserResource;
  onOpenChange: (open: boolean) => void;
  onUpdated: (user: UserResource) => void;
  onDeactivated: (user: UserResource) => void;
  onReactivated: (user: UserResource) => void;
  onLockoutCleared: (user: UserResource) => void;
}) {
  return (
    <Dialog open onOpenChange={onOpenChange}>
      <FormDialogContent>
        {/* Mounted per open, like the create form, so a half-typed rename — and,
            worse, a typed password — does not wait for the next person opened. */}
        <EditUserForm
          user={user}
          onUpdated={onUpdated}
          onDeactivated={onDeactivated}
          onReactivated={onReactivated}
          onLockoutCleared={onLockoutCleared}
        />
      </FormDialogContent>
    </Dialog>
  );
}

/**
 * What identifies this person, and does not change.
 *
 * The staff code keeps the mono face and tabular figures it has in the table:
 * it is read aloud and typed back in, and the two places it appears must look
 * like the same value.
 */
function Identity({ user }: { user: UserResource }) {
  return (
    // The one contrast trap in this system: `text-muted-foreground` on
    // `bg-muted` measures 4.39:1 and misses AA. The value here is
    // `text-foreground` for that reason; the label takes the signage class.
    <div className="flex flex-wrap items-start gap-x-10 gap-y-4 rounded-2xl bg-muted px-4 py-3">
      <div className="space-y-1.5">
        <p className={fieldLabel}>Staff code</p>
        <p className="font-mono text-foreground numeric">{user.staffCode}</p>
      </div>
      {isDeactivated(user) && (
        <div className="space-y-1.5">
          <p className={fieldLabel}>Standing</p>
          {/* `outline` and not `destructive`, the same as in the list. The end
              of somebody's employment is an ordinary event, and red is kept for
              a system that refused something or is about to. */}
          <Badge variant="outline">Deactivated</Badge>
        </div>
      )}
    </div>
  );
}

function EditUserForm({
  user,
  onUpdated,
  onDeactivated,
  onReactivated,
  onLockoutCleared,
}: {
  user: UserResource;
  onUpdated: (user: UserResource) => void;
  onDeactivated: (user: UserResource) => void;
  onReactivated: (user: UserResource) => void;
  onLockoutCleared: (user: UserResource) => void;
}) {
  const client = useApiClient();
  const { session } = useSession();

  const form = useForm({
    resolver: zodResolver(editUserSchema),
    mode: 'onTouched',
    defaultValues: { displayName: user.displayName, role: user.role, password: '', pin: '' },
  });

  const [stage, setStage] = useState<Stage>('editing');
  const [formError, setFormError] = useState<string | null>(null);

  // One key for as long as this form is open. The dialog unmounts on success, so
  // the next attempt gets a fresh one; a refusal rolls the claim back
  // server-side, so a corrected resubmit re-claims cleanly. Where this component
  // makes more than one kind of write, the same key is safe across them: the
  // API's record is unique on `(scope, key)`, and each of those writes is its own
  // scope.
  const [idempotencyKey] = useState(newIdempotencyKey);

  const [refusal, setRefusal] = useState<string | null>(null);

  // The three lifecycle actions are not submits of this form, so they carry
  // their own flag rather than borrowing `isSubmitting`.
  const [acting, setActing] = useState(false);

  const pending = form.formState.isSubmitting || acting;

  const roleRef = useRef<HTMLButtonElement>(null);

  // Watched rather than held in a second copy: the hint under the picker has to
  // follow the value the resolver will actually be given.
  const role = form.watch('role');

  // Whether this is the person doing the editing. `session.user.id` is the id
  // the server minted and returned at sign-in, so this is a comparison of two
  // server-issued values rather than a guess from a name — and the API refuses
  // both of the things it gates whatever this says, so being wrong here costs a
  // refusal rather than an escalation.
  const isSelf = session?.user.id === user.id;

  const gone = isDeactivated(user);
  const locked = isLockedOut(user);

  // Exactly the condition `clearLockout` acts on at the API: it returns the
  // person unchanged, and writes no audit row, when there is neither a lockout
  // nor a count. Offering the control outside that would teach an owner that it
  // does nothing.
  const hasFailedAttempts = locked || user.failedAttempts > 0;

  // `isDirty` rather than a hand-written comparison: the defaults above are the
  // person as they stand, so react-hook-form is already answering exactly this
  // question and a second answer could disagree with it.
  const changed = form.formState.isDirty;

  const handleSubmit = async ({ displayName, role: chosen, password, pin }: EditUserValues) => {
    if (pending) return;

    setFormError(null);

    try {
      // Only what actually differs. `UpdateUserDto` is a patch and every field
      // on it is optional, so restating the current name puts a rename in the
      // park's audit trail that nobody performed — and restating the current
      // role would run the last-owner lock for a change that removes nobody.
      //
      // The shape comes from the generated client, which is also what makes
      // `staffCode`'s absence structural: there is no such field to send.
      const body: UsersController_update_v1Body = {
        ...(displayName === user.displayName ? {} : { displayName }),
        ...(chosen === user.role ? {} : { role: chosen }),
        ...(password === '' ? {} : { password }),
        ...(pin === '' ? {} : { pin }),
      };

      onUpdated(
        await client.request(UsersController_update_v1, {
          path: { id: user.id },
          body,
          headers: { [IDEMPOTENCY_KEY_HEADER]: idempotencyKey },
        }),
      );
    } catch (error) {
      if (
        error instanceof ApiError &&
        (error.code === 'IDENTITY_ROLE_ESCALATION' || error.code === 'IDENTITY_LAST_OWNER')
      ) {
        // Both are statements about the role and neither is about the form. A
        // page-level refusal here would read as "you cannot edit people", which
        // is not what happened — the name beside it was perfectly acceptable.
        //
        // Beside the field, and the focus goes with it: somebody who submitted
        // with the keyboard is otherwise left with the caret on the button and a
        // message they have to go and find. A ref rather than `setFocus`,
        // because a Select trigger is not a registered input for it to reach.
        form.setError('role', { message: messageForFailure(error) });
        roleRef.current?.focus();
      } else {
        setFormError(messageForFailure(error));
      }

      // Nothing is cleared, including the credentials. The retry is one
      // corrected field, and re-typing a password to fix a role is a cruelty
      // this form is in no position to inflict.
    }
  };

  const handleDeactivate = async () => {
    if (pending) return;

    setFormError(null);
    setActing(true);

    try {
      onDeactivated(
        await client.request(UsersController_deactivate_v1, {
          path: { id: user.id },
          headers: { [IDEMPOTENCY_KEY_HEADER]: idempotencyKey },
        }),
      );
    } catch (error) {
      if (error instanceof ApiError && error.code === 'IDENTITY_LAST_OWNER') {
        // The dialog stops asking. Everything else here can be retried by
        // pressing the same button again; this cannot succeed until somebody
        // else holds the owner role, so leaving the offer up would be an
        // invitation to press it until it works.
        setRefusal(messageForFailure(error));
        setStage('refused-last-owner');
      } else {
        setFormError(messageForFailure(error));

        // Back to the form, because the confirmation's copy is about a thing
        // that did not happen and leaving it up beside a refusal reads as though
        // it half did.
        setStage('editing');
      }
    } finally {
      setActing(false);
    }
  };

  const handleReactivate = async () => {
    if (pending) return;

    setFormError(null);
    setActing(true);

    try {
      onReactivated(
        await client.request(UsersController_reactivate_v1, {
          path: { id: user.id },
          headers: { [IDEMPOTENCY_KEY_HEADER]: idempotencyKey },
        }),
      );
    } catch (error) {
      // No confirmation before this and no refusal specific to it: restoring
      // somebody's access is reversible by the button beside it, and the API
      // guards nothing here that it does not guard on the way out.
      setFormError(messageForFailure(error));
    } finally {
      setActing(false);
    }
  };

  const handleClearLockout = async () => {
    if (pending) return;

    setFormError(null);
    setActing(true);

    try {
      onLockoutCleared(
        await client.request(UsersController_clearLockout_v1, {
          path: { id: user.id },
          headers: { [IDEMPOTENCY_KEY_HEADER]: idempotencyKey },
        }),
      );
    } catch (error) {
      setFormError(messageForFailure(error));
    } finally {
      setActing(false);
    }
  };

  if (stage === 'confirming-deactivate') {
    return (
      <>
        <DialogHeader>
          <DialogTitle>Deactivate {user.displayName}?</DialogTitle>
          <DialogDescription>Deactivating is not deleting.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 text-sm text-foreground">
          {/* The promise the ticket is built around, and the reason this
              confirmation exists at all. An owner hesitating over a departing
              employee's records deserves to know they survive before pressing
              the button rather than after. */}
          <p>
            Everything they ever did keeps their name on it. Sales, tickets, day closes and audit
            entries stay attributed to them, so a record read next year still resolves to a person
            rather than to a gap.
          </p>
          <p>
            They cannot sign in again, anywhere they are signed in now ends immediately, and their
            staff code <span className="font-mono text-foreground numeric">{user.staffCode}</span>{' '}
            stays theirs for ever — it is never issued to anybody else.
          </p>
          <p>If they come back, reactivate them and they sign in under the same staff code.</p>
        </div>

        <DialogFooter>
          <Button variant="outline" disabled={pending} onClick={() => setStage('editing')}>
            Keep their access
          </Button>
          <Button variant="destructive" disabled={pending} onClick={() => void handleDeactivate()}>
            {pending ? 'Deactivating…' : 'Yes, deactivate them'}
          </Button>
        </DialogFooter>
      </>
    );
  }

  if (stage === 'refused-last-owner') {
    return (
      <>
        <DialogHeader>
          <DialogTitle>{user.displayName} is the park&rsquo;s last owner</DialogTitle>
          <DialogDescription>Nothing has changed.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 text-sm text-foreground">
          {/* Rendered as prose and not as a destructive alert. Red says the
              system refused something; that is true here and unhelpful — the
              park is being kept out of a room it could not let itself back into,
              and an owner reading this needs the rule rather than the failure. */}
          <p>{refusal}</p>
          <p>
            There is no way back into the back office without an owner: no reset by email and no
            self-registration. Give somebody else the owner role, and this person can then be
            deactivated.
          </p>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setStage('editing')}>
            Back
          </Button>
        </DialogFooter>
      </>
    );
  }

  return (
    <>
      <DialogHeader>
        <DialogTitle>{user.displayName}</DialogTitle>
        <DialogDescription>
          What they are called, what they may do, and whether they can sign in at all.
        </DialogDescription>
      </DialogHeader>

      {/* One column, per docs/ui-conventions.md. `noValidate` because the
          browser's own bubbles are not this app's error language. */}
      <Form {...form}>
        <form
          className="space-y-5"
          noValidate
          onSubmit={(event) => void form.handleSubmit(handleSubmit)(event)}
        >
          <Identity user={user} />

          <p className="text-sm text-muted-foreground">
            A staff code cannot be changed and is never reissued. It names who did something in
            records that outlive employment, so changing one would re-point every one of them at a
            code their reader does not recognise.
          </p>

          {hasFailedAttempts && (
            <Lockout user={user} pending={pending} onClear={handleClearLockout} />
          )}

          <FormField
            control={form.control}
            name="displayName"
            render={({ field }) => (
              <FormItem>
                <FormLabel required className={fieldLabel}>
                  Name
                </FormLabel>
                <FormControl
                  render={
                    <Input
                      className={`${fieldSurface} h-11`}
                      required
                      placeholder="Binu Mathew"
                      autoComplete="off"
                      {...field}
                    />
                  }
                />
                <FormDescription>
                  Renaming somebody renames them everywhere, including beside things they did before
                  today. A person who changed their name has changed it.
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="role"
            render={({ field }) => (
              <FormItem>
                <FormLabel id="edit-user-role-label" className={fieldLabel}>
                  Role
                </FormLabel>
                {/* Not wrapped in `FormControl`: the trigger is a button rather
                    than the input the generated id belongs on, and it already
                    carries its own accessible name. */}
                <Select
                  value={role}
                  items={USER_ROLE_ITEMS}
                  disabled={isSelf}
                  onValueChange={(next) => field.onChange(next as UserRole)}
                >
                  <SelectTrigger
                    id="edit-user-role"
                    ref={roleRef}
                    aria-label="Role"
                    aria-invalid={form.formState.errors.role !== undefined}
                    aria-describedby={
                      form.formState.errors.role === undefined
                        ? 'edit-user-role-hint'
                        : 'edit-user-role-error'
                    }
                    className={`${fieldSurface} h-11 w-full`}
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      {USER_ROLES.map((option) => (
                        <SelectItem key={option} value={option}>
                          {labelForRole(option)}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  </SelectContent>
                </Select>
                {form.formState.errors.role === undefined ? (
                  <p id="edit-user-role-hint" className="text-sm text-muted-foreground">
                    {isSelf
                      ? 'You cannot change your own role, whatever role you hold. Ask another owner to change it for you — it is one of the two rules that keep the park out of a room it cannot let itself back into.'
                      : hintForRole(role)}
                  </p>
                ) : (
                  <FormMessage id="edit-user-role-error" />
                )}
              </FormItem>
            )}
          />

          {isSelf ? (
            // Setting your own credential through this route is refused by the
            // API — it exists so somebody else can hand you one, and handing
            // yourself one proves nothing. The route that does it re-proves the
            // current password first, so this points there rather than rendering
            // two fields whose only possible outcome is a validation failure.
            <div className="space-y-1 rounded-2xl bg-muted px-4 py-4">
              <p className="text-sm font-medium text-foreground">Your own password and PIN</p>
              <p className="text-sm text-foreground/70">
                Changed under{' '}
                <Link
                  to={paths.changeCredential}
                  className="underline underline-offset-4 outline-none focus-visible:ring-3 focus-visible:ring-ring/30"
                >
                  Change credential
                </Link>
                , which asks for your current password first. Nobody sets their own here, including
                an owner.
              </p>
            </div>
          ) : (
            <div className="space-y-4 rounded-2xl bg-muted px-4 py-4">
              <div className="space-y-1">
                <p className="text-sm font-medium text-foreground">Set a credential for them</p>
                {/* Stated before either field rather than under them, because it
                    changes what somebody types — and because setting one ends
                    every session they have open, which is usually the point of
                    resetting it and is a surprise if nobody said so. */}
                <p className="text-sm text-foreground/70">
                  Leave both blank to change nothing. Whatever you set here, they must replace it
                  before they can do anything else, and any session they have open right now ends
                  the moment you save.
                </p>
              </div>

              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className={fieldLabel}>Back-office password</FormLabel>
                    <FormControl
                      render={
                        <PasswordInput
                          className={`${fieldSurface} h-11`}
                          placeholder="Leave blank to change nothing"
                          // `new-password`, so the editor's own saved credential
                          // is not offered into a field that would then set it
                          // on somebody else.
                          autoComplete="new-password"
                          {...field}
                        />
                      }
                    />
                    <FormDescription className="text-foreground/70">
                      For signing in to the back office. Leave it blank for staff who only ever sign
                      in at a terminal.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="pin"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className={fieldLabel}>Terminal PIN</FormLabel>
                    <FormControl
                      render={
                        <Input
                          type="password"
                          className={`${fieldSurface} h-11 font-mono tracking-widest numeric`}
                          placeholder="4827"
                          autoComplete="new-password"
                          inputMode="numeric"
                          {...field}
                        />
                      }
                    />
                    <FormDescription className="text-foreground/70">
                      4 to 6 digits, typed on a touchscreen at a gate or a till.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          )}

          {/* Above the submit and it stays there — a toast vanishes, and
              somebody who looked away has no way to get it back. */}
          {formError !== null && (
            <Alert variant="destructive">
              <IconAlertTriangle />
              <AlertDescription>{formError}</AlertDescription>
            </Alert>
          )}

          <DialogFooter className="sm:justify-between">
            {/* Not the screen's accent and not a row control: the end of
                somebody's access sits at the far end of their own form, where it
                is found deliberately rather than brushed past. */}
            {gone ? (
              <Button
                type="button"
                variant="outline"
                disabled={pending}
                onClick={() => void handleReactivate()}
              >
                {pending ? 'Reactivating…' : 'Reactivate this person'}
              </Button>
            ) : (
              <Button
                type="button"
                variant="outline"
                className="text-destructive"
                disabled={pending}
                onClick={() => setStage('confirming-deactivate')}
              >
                Deactivate this person
              </Button>
            )}

            {/* Disabled until something differs. An empty PATCH is
                `VALIDATION_FAILED` at the API, and one that restates what is
                already there spends an audit row saying somebody opened a
                dialog. */}
            <Button type="submit" disabled={pending || !changed}>
              {pending ? 'Saving…' : 'Save changes'}
            </Button>
          </DialogFooter>
        </form>
      </Form>
    </>
  );
}

/**
 * The lockout, and the owner's override of it (FR-121a).
 *
 * **Load-bearing rather than a convenience, and placed as though it were.** The
 * lockout is counted per staff code, and a staff code is printed on a badge and
 * typed at a till in public — so anybody who knows one can lock a named person
 * out at will by getting it wrong five times. Without this button that is a
 * denial of service with no remedy but waiting, which is why it sits at the top
 * of the form where an owner finds it under pressure rather than in a menu.
 *
 * **It says how they got there and not when it lifts**, which was forced and is
 * now a choice. `lockedUntil` was `Record<string, never> | null` on the
 * generated shape and there was no time to format; as of the 10 Aug 2026
 * regeneration it is a real `string | null` (`#209`), so this screen could show
 * the clock and does not yet. `failedAttempts` is what is shown, and it answers
 * the question an owner is actually asking — whether somebody is being locked
 * out repeatedly — rather than the one a countdown answers.
 */
function Lockout({
  user,
  pending,
  onClear,
}: {
  user: UserResource;
  pending: boolean;
  onClear: () => Promise<void>;
}) {
  const locked = isLockedOut(user);

  return (
    <div className="space-y-3 rounded-2xl bg-muted px-4 py-4">
      <div className="space-y-1">
        <p className="text-sm font-medium text-foreground">
          {locked ? 'Locked out' : 'Failed sign-in attempts'}
        </p>
        <p className="text-sm text-foreground/70">
          <span className="font-mono text-foreground numeric">{user.failedAttempts}</span> failed
          attempts in a row.{' '}
          {locked
            ? 'Five locks a staff code for fifteen minutes, and clearing it now lets them work rather than stand at a till waiting out a timer. It is also the remedy when somebody has been locked out on purpose — a staff code is on a badge, and anybody who knows one can get it wrong five times.'
            : 'Five in a row locks the code for fifteen minutes. Clearing the count now takes them back from the edge of it.'}
        </p>
      </div>

      <Button type="button" variant="outline" disabled={pending} onClick={() => void onClear()}>
        {locked ? 'Clear the lockout' : 'Clear the count'}
      </Button>
    </div>
  );
}
